import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "@phosphor-icons/react";
import { traceBatch } from "../lib/api";
import { fmtMoney, fmtRate, fmtDate } from "../lib/format";
import { useWorkflowOptional } from "../workflow/WorkflowContext";
import { toast } from "./ui/sonner";
import { labelFor } from "../lib/enumLabels";
import { buyerIdentity } from "../lib/buyerIdentity";

// v0.6.12 A2-192 — one chip style, used by all three party chips.
// Extracted rather than repeated: designTokens.test.mjs counts
// arbitrary text-[Npx] OCCURRENCES, and the three copies this
// replaces were already two of them.
const CHIP = "tabular-nums text-[11px] px-1.5 py-0.5 rounded";
const CHIP_NEUTRAL = `${CHIP} bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100`;
const CHIP_MUTED = `${CHIP} bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400`;
const CHIP_WARN = `${CHIP} bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300`;

// Portalled surfaces a click on which must not close the drawer.
const KEEP_OPEN_SELECTOR =
  '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], ' +
  "[data-radix-popper-content-wrapper], [data-sonner-toaster]";

export const TraceDrawer = ({ line, onClose }) => {
  const { t } = useTranslation();
  const wf = useWorkflowOptional();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!line) return;
    // v0.6.8 fix — this used to have no cancellation guard, unlike
    // every sibling data-fetching effect in this file's batch. The
    // drawer stays mounted while `line` changes (WorkflowLayout.jsx
    // renders it persistently), and the table underneath stays
    // clickable while a fetch is in flight, so clicking a second row
    // before the first row's traceBatch() resolved let the STALE
    // response overwrite the newer row's already-rendered data —
    // same shape as the CalcProgressCard race condition fixed
    // earlier this session.
    let cancelled = false;
    setLoading(true);
    traceBatch(line.transaction_ids, wf?.calc?.id)
      .then((r) => { if (!cancelled) setRows(r); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [line, wf?.calc?.id]);

  // Operator, 2026-10-11: "make that side info screen disappear on
  // clicking on main page instead of cross". A click anywhere outside
  // the drawer closes it; Esc does too. No backdrop on purpose: one
  // would swallow the click, so Inspect on another row would need two
  // clicks. Closing on pointerdown lets that same click's Inspect
  // reopen the drawer on the new row. Clicks inside dialogs, menus and
  // toasts (portals, so outside the drawer's DOM) are not "the page".
  const panelRef = useRef(null);
  useEffect(() => {
    if (!line) return undefined;
    const onPointerDown = (e) => {
      const el = e.target;
      if (!(el instanceof Element) || !el.isConnected) return;
      if (panelRef.current && panelRef.current.contains(el)) return;
      if (el.closest(KEEP_OPEN_SELECTOR)) return;
      onClose();
    };
    const onKeyDown = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [line, onClose]);

  const agg = React.useMemo(() => {
    const sum = (k) => rows.reduce((a, r) => a + (parseFloat(r[k]) || 0), 0);
    return {
      net: line?.net_total ?? (rows.length ? sum("net_amount").toFixed(2) : null),
      vat: line?.vat_total ?? (rows.length ? sum("vat_amount").toFixed(2) : null),
      gross: line?.gross_total ?? (rows.length ? sum("gross_amount").toFixed(2) : null),
    };
  }, [rows, line]);

  if (!line) return null;

  return (
    <div
      ref={panelRef}
      data-testid="trace-drawer"
      className="fixed inset-y-0 right-0 w-full md:w-[640px] bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 z-50 overflow-auto"
    >
      <div className="border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between bg-white dark:bg-slate-900">
        <div>
          <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            {t("ui.trace_drawer.title")}
          </div>
          <div className="tabular-nums text-lg text-teal-700 dark:text-teal-300">
            {line.country
              ? <>{line.country} · {fmtRate(line.rate)} · {labelFor("tax_category", line.tax_category)}</>
              : t("ui.trace_drawer.transactions_label", { count: line.txn_count ?? rows.length })}
          </div>
        </div>
        <button
          data-testid="trace-drawer-close"
          onClick={onClose}
          className="text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:text-teal-400 rounded-xl p-1"
        >
          <X size={20} weight="bold" />
        </button>
      </div>

      {/* v0.6.10 — totals read as data, not paragraphs: small-caps
          sans labels, tabular monospaced figures so digits line up column
          to column. VAT is the number the operator is actually
          checking, so it carries the accent; a zero says so in words
          rather than leaving them to read six characters. */}
      <div className="border-b border-slate-200 dark:border-slate-700 px-4 py-3 grid grid-cols-3 gap-3">
        {[
          ["net", agg.net, false],
          ["vat", agg.vat, true],
          ["gross", agg.gross, false],
        ].map(([key, val, accent]) => (
          <div key={key}>
            <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              {t(`ui.trace_drawer.${key}`)}
            </div>
            <div
              className={`tabular-nums text-lg  ${
                accent
                  ? "text-teal-700 dark:text-teal-300"
                  : "text-slate-900 dark:text-slate-100"
              }`}
            >
              {fmtMoney(val)}
            </div>
            {accent && Number(val) === 0 && (
              <div className="text-[9px] text-slate-400 dark:text-slate-500">
                {t("ui.trace_drawer.no_vat", { defaultValue: "none charged" })}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="px-4 py-3">
        <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">
          {t("ui.trace_drawer.transactions_label", { count: line.txn_count ?? rows.length })}
        </div>
        {loading && <div className="text-xs text-slate-500 dark:text-slate-400">{t("ui.trace_drawer.loading")}</div>}
        <div className="space-y-2">
          {rows.map((tx) => (
            <div
              key={tx.id}
              data-testid={`trace-txn-${tx.id}`}
              data-raw-tx-row={tx.order_id}
              data-order-id={tx.order_id}
              className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-xs"
            >
              {/* v0.6.10 — neither "CLIENT_TEMPLATE#523" nor
                  "TEST_P1_SalesForce_0643" tells a non-technical
                  operator anything about the row, so neither leads.
                  What identifies a sale to a human is when it
                  happened, what was sold, where it went and for how
                  much — in that order. Both machine references stay,
                  demoted to the footer, because they are how you
                  find the row again in the source file. */}
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[13px] font-medium text-slate-900 dark:text-slate-50">
                  {fmtDate(tx.transaction_date)}
                </span>
                {tx.supply_type && (
                  <span
                    title={t("ui.trace_drawer.supply_type_label")}
                    className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-teal-50 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200"
                  >
                    {t(`supply_types.${tx.supply_type}`, { defaultValue: tx.supply_type })}
                  </span>
                )}
              </div>
              <div
                data-testid={`trace-parties-${tx.id}`}
                className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-slate-600 dark:text-slate-300"
              >
                <span className="inline-flex items-center gap-1.5">
                  <span className={CHIP_NEUTRAL}>
                    {tx.seller_country || "—"}
                  </span>
                  <span className="text-slate-400 dark:text-slate-500">→</span>
                  <span className={CHIP_NEUTRAL}>
                    {tx.buyer_country || "—"}
                  </span>
                </span>
                {/* v0.6.12 A2-192 — who the buyer is. A blank cannot
                    distinguish "private individual, correctly no
                    number" from "business sale, number missing", and
                    the second is a flagged condition. */}
                {(() => {
                  const id = buyerIdentity(tx);
                  return (
                    <span
                      data-testid={`trace-buyer-identity-${tx.id}`}
                      data-buyer-identity-kind={id.kind}
                      className={
                        id.kind === "number" ? CHIP_NEUTRAL
                          : id.kind === "absent" ? CHIP_WARN
                            : CHIP_MUTED
                      }
                    >
                      {id.text}
                    </span>
                  );
                })()}
              </div>
              {(tx.property_country || tx.event_country || tx.service_country || tx.handover_country) && (
                <div
                  data-testid={`trace-place-of-supply-${tx.id}`}
                  className="mt-2 rounded-md bg-slate-50 dark:bg-slate-800/60 px-2 py-1.5 space-y-0.5"
                >
                  <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {t("ui.trace_drawer.place_of_supply_label")}
                  </div>
                  {tx.property_country && (
                    <div data-testid={`trace-property-country-${tx.id}`} className="flex justify-between text-slate-600 dark:text-slate-300">
                      <span>{t("ui.trace_drawer.property_country_label")}</span>
                      <span className="tabular-nums text-slate-900 dark:text-slate-50">{tx.property_country}</span>
                    </div>
                  )}
                  {tx.event_country && (
                    <div data-testid={`trace-event-country-${tx.id}`} className="flex justify-between text-slate-600 dark:text-slate-300">
                      <span>{t("ui.trace_drawer.event_country_label")}</span>
                      <span className="tabular-nums text-slate-900 dark:text-slate-50">{tx.event_country}</span>
                    </div>
                  )}
                  {tx.service_country && (
                    <div data-testid={`trace-service-country-${tx.id}`} className="flex justify-between text-slate-600 dark:text-slate-300">
                      <span>{t("ui.trace_drawer.service_country_label")}</span>
                      <span className="tabular-nums text-slate-900 dark:text-slate-50">{tx.service_country}</span>
                    </div>
                  )}
                  {tx.handover_country && (
                    <div data-testid={`trace-handover-country-${tx.id}`} className="flex justify-between text-slate-600 dark:text-slate-300">
                      <span>{t("ui.trace_drawer.handover_country_label")}</span>
                      <span className="tabular-nums text-slate-900 dark:text-slate-50">{tx.handover_country}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                {[["net", tx.net_amount], ["vat", tx.vat_amount],
                  ["gross", tx.gross_amount]].map(([k, v]) => (
                  <div key={k}>
                    <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      {t(`ui.trace_drawer.${k}`)}
                    </div>
                    <div className="tabular-nums  text-slate-900 dark:text-slate-50">
                      {fmtMoney(v)}
                    </div>
                  </div>
                ))}
              </div>
              {tx.review_flags?.length > 0 && (
                /* v0.6.10 — was the raw enum names joined by commas
                   ("BUYER_VAT_NUMBER_MISSING, GB_TO_EU_ZERO_RATED_
                   EXPORT"). The queue already has a human title for
                   every flag; showing the enum here made the operator
                   translate between two names for one thing. */
                <div
                  data-testid={`trace-flags-${tx.id}`}
                  className="mt-2 flex flex-wrap gap-1"
                >
                  {tx.review_flags.map((f) => (
                    <span
                      key={f}
                      title={f}
                      className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200"
                    >
                      {t(`flag_titles.${f}`, { defaultValue: f })}
                    </span>
                  ))}
                </div>
              )}
              {/* v0.4.79 D2 — explainer when VIES validation
                  reclassified the row from B2B reverse-charge to
                  a B2C distance sale. */}
              {(tx.review_flags || []).includes("VAT_NUMBER_INVALID") && (
                <div
                  data-testid={`trace-vies-explainer-${tx.id}`}
                  className="mt-2 text-xs bg-amber-50 dark:bg-amber-900/40 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100 rounded p-2 leading-snug"
                >
                  {/* A2-468 — say what actually happened to THIS row: a
                      distance sale, an Art 45 service, or goods taxed where
                      they leave from (no longer every row the same text). */}
                  {(tx.review_flags || []).includes("B2B_RECLASSIFIED_TO_DISTANCE_SALE") && tx.supply_type !== "GOODS"
                    ? t("ui.trace_drawer.vies_invalid_explainer_destination", { defaultValue: "Buyer VAT number failed VIES validation — the customer is treated as a consumer, so the service is taxed in the customer's country (Art 56(2) long-term hire, Art 58 digital services). Re-confirm the number with the buyer, or amend it." })
                    : (tx.review_flags || []).includes("B2B_RECLASSIFIED_TO_DISTANCE_SALE")
                    ? t("ui.trace_drawer.vies_invalid_explainer", { defaultValue: "Buyer VAT number failed VIES validation — treated as B2C distance sale per Art 33 Directive 2006/112/EC. Operator should re-confirm with the buyer or amend the registration." })
                    : (tx.review_flags || []).includes("B2B_SERVICE_RECLASSIFIED_TO_ART45_LOCAL")
                      ? t("ui.trace_drawer.vies_invalid_explainer_service", { defaultValue: "Buyer VAT number failed VIES validation — the customer is treated as a consumer, so the service is taxed where your client is established (Art 45 Directive 2006/112/EC). Re-confirm the number with the buyer, or amend it." })
                      : tx.supply_type === "GOODS" && tx.seller_country !== tx.buyer_country
                        ? t("ui.trace_drawer.vies_invalid_explainer_goods", { defaultValue: "Buyer VAT number failed VIES validation — the 0 % intra-EU rate does not apply (Art 138(1)(b)), so the goods are taxed as your own sale where they leave from (Art 32). Re-confirm the number with the buyer, or amend it." })
                        : t("ui.trace_drawer.vies_invalid_explainer_other", { defaultValue: "Buyer VAT number failed VIES validation — the customer is treated as a consumer. Re-confirm the number with the buyer, or amend it." })}
                </div>
              )}
              {/* v0.4.31 D2 — routing trace. Click-expand disclosure
                  showing every classifier check evaluated, in
                  insertion order (the order the classifier
                  evaluated them — NOT alphabetical; the engine is
                  a first-match-wins ordered fold). Each row shows
                  check name, Y/N outcome, and the value(s)
                  consulted. */}
              {tx.routing_trace && tx.routing_trace.length > 0 && (
                <details data-testid={`trace-routing-${tx.id}`} className="mt-2 border-t border-slate-200 dark:border-slate-700 pt-2">
                  <summary className="cursor-pointer select-none text-[9px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 hover:text-teal-700 dark:hover:text-teal-300">
                    {t("ui.trace_drawer.routing_decision")} ({tx.routing_trace.length})
                  </summary>
                  <ol className="mt-1 ml-2 space-y-0.5 text-[10px]">
                    {tx.routing_trace.map((step, i) => (
                      <li key={i} data-testid={`trace-step-${tx.id}-${i}`} className="flex items-start gap-2">
                        <span className={step.outcome ? "text-emerald-700 dark:text-emerald-300" : "text-slate-600 dark:text-slate-300"}>
                          {step.outcome ? "✓" : "✗"}
                        </span>
                        <span className="text-slate-500 dark:text-slate-400">{step.check}</span>
                        {step.consulted !== null && step.consulted !== undefined && (
                          <span className="text-slate-500 dark:text-slate-400 ml-auto">{JSON.stringify(step.consulted)}</span>
                        )}
                      </li>
                    ))}
                  </ol>
                </details>
              )}
              {tx.routing_trace === null || tx.routing_trace === undefined ? (
                <div
                  data-testid={`trace-routing-na-${tx.id}`}
                  className="mt-2 text-[10px] text-slate-500 dark:text-slate-400"
                >
                  {/* v0.6.10 — the "Populate trace" button that lived
                      here is GONE, not restyled. It re-ran the entire
                      calculation to fill this in, and could never
                      succeed: classify.py built the trace, and
                      calc_persist never saved it, so the re-run
                      rebuilt and discarded it exactly as before. The
                      operator paid a full recalculation and their
                      whole review queue for a guaranteed no-op.
                      calc_persist now saves the trace, so this line
                      only appears for rows last calculated before
                      that fix — and the next ordinary calculation
                      clears it, with no special button needed. */}
                  {t("ui.trace_drawer.routing_not_available")}
                </div>
              ) : null}
              {/* v0.6.10 — provenance, demoted from the headline it
                  used to occupy. The operator needs it to find the
                  row in the source file, not to identify it here. */}
              <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 text-[10px] text-slate-400 dark:text-slate-500">
                {t("ui.trace_drawer.order_label")}{" "}
                <span className="tabular-nums text-slate-500 dark:text-slate-400">{tx.order_id || "—"}</span>
                {" · "}
                {t("ui.trace_drawer.source_ref_label")}{" "}
                {/* v0.6.8 fix kept — tx.adapter is optional on the
                    backend, and reading it unguarded once blanked the
                    whole page, there being no error boundary. */}
                <span
                  data-testid={`trace-source-${tx.id}`}
                  className="text-slate-500 dark:text-slate-400"
                >
                  {labelFor("adapter", tx.adapter)}{" "}
                  {t("ui.trace_drawer.row_label")}{" "}
                  <span className="tabular-nums">{tx.source_row}</span>
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
